import classNames from 'classnames'
import { Field, FieldInputProps } from 'formik'
import useTranslate from 'marketing-site/src/library/hooks/useTranslate'
import { Color } from 'marketing-site/src/library/utils'
import { NewBrandingHoc } from 'marketing-site/src/library/utils/NewBrandingHoc'
import React, { useCallback, useState } from 'react'
import { Chevron } from '../../../Chevron'
import { IFieldValue, ISelectField } from '../../@types/FormField'
import { sanitizeText } from '../../Field'
import { IFormFieldProps } from '../../Field.interface'
import { Select as SelectLegacy } from './Select-legacy'
import { SelectOptions } from './SelectOptions'

export type ISelectFieldProps = IFormFieldProps<ISelectField>

export const testIds = {
  label: 'form-field-select-label',
  select: 'form-field-select-select',
  option: (value: string): string => `form-field-select-option-${value}`,
  placeholder: 'form-field-select-placeholder',
}

interface ISelectComponent {
  field: FieldInputProps<any>
  isDisabled: boolean
  placeholder: string
  onChange: (e: React.ChangeEvent<any>) => void
  required?: boolean
  options: IFieldValue[]
}

const SelectComponent = ({
  field,
  isDisabled,
  placeholder,
  onChange,
  required,
  options,
}: ISelectComponent) => {
  const { name } = field
  const [isOptionSelected, setIsOptionSelected] = useState<boolean>(placeholder ? false : true)

  const fieldId = `${name}_field`
  const labelId = `${name}_label`

  const customOnChange = useCallback(
    (event: React.ChangeEvent<any>) => {
      setIsOptionSelected(true)
      onChange?.(event)
      field.onChange(event)
    },
    [onChange, field.onChange, setIsOptionSelected],
  )

  const selectClassNames = classNames({ grayed: !isOptionSelected })

  return (
    <>
      <div className="select-container">
        {/* eslint-disable-next-line jsx-a11y/no-onchange*/}
        <select
          {...field}
          id={fieldId}
          className={selectClassNames}
          disabled={isDisabled}
          aria-labelledby={labelId}
          aria-required={required}
          data-testid={testIds.select}
          onChange={customOnChange}
        >
          {placeholder && (
            <option value="" data-testid={testIds.placeholder} disabled hidden selected>
              {placeholder}
            </option>
          )}
          <SelectOptions values={options} />
        </select>
        <span className="caret" aria-hidden="true">
          <Chevron brp={true} />
        </span>
      </div>
      <style jsx>{`
        .caret {
          position: absolute;
          transform: translate(-29px, 24px);
        }
        .select-container {
          position: relative;
        }
      `}</style>
    </>
  )
}

export const SelectDefault = React.memo((field: ISelectFieldProps): React.ReactElement => {
  const { id, isDisabled, label, required, values, placeholder, enableValidation, hasError } = field

  const labelClassNames = classNames('form-field-select', {
    'has-error': enableValidation && hasError,
  })

  const { getTranslatedValidationErrorIfAny } = useTranslate()

  return (
    <div>
      <label className={labelClassNames} data-testid={testIds.label}>
        {label && <span className="select-label">{sanitizeText(label)}</span>}

        <Field
          name={id}
          validate={(value: string) => getTranslatedValidationErrorIfAny(field, value)}
          component={SelectComponent}
          isDisabled={isDisabled}
          required={required}
          options={values}
          placeholder={placeholder}
          onChange={field.onChange}
        />
      </label>
      <style jsx>{`
        .form-field-select {
          .select-label {
            display: block;
            margin-bottom: var(--form-label-spacing-y, 0);
          }

          :global(select.grayed) {
            color: ${Color.UIGray};
          }

          :global(select) {
            width: 100%;
            -webkit-appearance: none;
            background-color: ${Color.White};
            line-height: 1.5em;
            color: ${Color.Slate};
            border: 2px solid ${Color.Slate};
            border-radius: 6px;
            padding: 12px 17px;
            min-height: 1.9em;
            font: inherit;
            resize: none;
            outline: none;
          }

          &.has-error {
            :global(select) {
              border-color: ${Color.UIError};
            }
          }
        }
      `}</style>
    </div>
  )
})

export const Select = NewBrandingHoc(SelectLegacy, SelectDefault)
